// DS6.1.5
import React from 'react';
import isEqual from 'lodash/isEqual';
import { useUniqueId } from '@wix/da-hooks/pkg/useUniqueId';
import { useDropdownItems } from '../../hooks/useDropdownItems';
import { NullableDropdownItem } from '../../types';
import StandardDropdownMenu, {
  StandardDropdownMenuProps,
} from '../StandardDropdown/StandardDropdownMenu';
import type { OffsetModifier, PopperPlacement } from '../../../popper/types';
import {
  PopperDropdownManager,
  PopperDropdownManagerProps,
} from '../../PopperDropdownLayout';
import PopperIconToggleButton, {
  PopperIconToggleButtonProps,
} from '../../toggles/PopperIconToggleButton';
import type { RequiresAccessibleLabel } from '@wix/da-a11y/pkg/typeHelpers';

interface BaseProps {
  items: NullableDropdownItem[];
  openOnHover?: boolean;
  placement?: PopperPlacement;

  popperManagerProps?: PopperDropdownManagerProps;
  standardMenuProps?: Omit<
    StandardDropdownMenuProps,
    'items' | 'role' | 'id' | 'menuScrollable'
  >;
  offsetModifier?: OffsetModifier;
}

// We must reassert RequiresAccessibleLabel here because the `Omit` messes with lower-leveling component typings
export type Props = RequiresAccessibleLabel<BaseProps> &
  Omit<PopperIconToggleButtonProps, 'aria-haspopup'>;

/**
 * Our DS `IconButton` with a context menu provided by our dropdown library.
 *
 * You can customize the popper or standard menu instance via `popperManagerProps` or `standardMenuProps` respectively.
 */
const IconButtonWithContextMenu: React.FC<Props> = React.memo(
  ({
    items,
    openOnHover,
    popperManagerProps,
    standardMenuProps,
    placement,
    offsetModifier,
    ...rest
  }) => {
    const { filteredItems } = useDropdownItems(items);

    const toggleId = useUniqueId();
    const dropdownId = useUniqueId();

    return (
      <PopperDropdownManager {...popperManagerProps} openOnHover={openOnHover}>
        <PopperIconToggleButton
          id={toggleId}
          aria-haspopup="menu"
          aria-controls={dropdownId}
          {...rest}
        />
        <StandardDropdownMenu
          id={dropdownId}
          role="menu"
          aria-labelledby={toggleId}
          menuScrollable
          items={filteredItems}
          placement={placement}
          offsetModifier={
            offsetModifier ||
            (openOnHover
              ? [
                  0, 0,
                ] /* no gap - to not hide the menu when mouse travels from the toggle to the menu */
              : undefined)
          }
          {...standardMenuProps}
        />
      </PopperDropdownManager>
    );
  },
  // Deep equal from lodash so we can compare the standard menu and popper manager prop collections
  isEqual
);
IconButtonWithContextMenu.displayName = 'IconButtonWithContextMenu';

export default IconButtonWithContextMenu;
